import React from 'react';
import { useIntl } from 'react-intl';
import { CHART_COLORS, GRADE_COLORS, BLACK } from 'shared/constants/colors';
import { GRADE, NPS, FIND } from 'shared/constants/questionCategories';
import theme from 'shared/constants/theme.json';
import { hasCategory } from 'shared/utils/categories';
import { percent } from 'shared/constants/math';

import { QUESTION_TYPES } from 'shared/constants/survey';
import Chart from './Chart';
import { colorChartParts, iconBase, uniqueSegmentId, getSegment, getGridOptions, getUniqueValues, getValueAxis } from './utils';
import { labelFormatter, tooltipFormatter, formatSegment } from './formatters';

const LABEL_MAX_LENGTH = 40;

const getAxisIcon = (question, value) => {
  if (!value) return '';

  const { categories, type } = question;

  if (type === QUESTION_TYPES.GRADE || hasCategory(categories, [GRADE])) {
    switch (value.optionValue) {
      case 1:
        return '{veryBad|} ';
      case 2:
        return '{bad|} ';
      case 3:
        return '{neutral|} ';
      case 4:
        return '{good|} ';
      case 5:
        return '{veryGood|} ';
      default:
        return '';
    }
  }

  if ([QUESTION_TYPES.FIND, QUESTION_TYPES.NPS].includes(type) || hasCategory(categories, [NPS, FIND])) {
    switch (value.optionValue) {
      case -100:
        return '{veryBad|} ';
      case 0:
        return '{neutral|} ';
      case 100:
        return '{veryGood|} ';
      default:
        return '';
    }
  }

  return '';
};

const getCategoryAxis = (segments, question, isVerticalBars) => {
  const data = getUniqueValues(segments).map(v => v.name);

  const horizontalLabelMaxLength = isVerticalBars ? 250 / data.length : 40;

  const shouldRotate = isVerticalBars
    && data.some(d => d.length > horizontalLabelMaxLength);

  // rotated labels have half max length.
  const labelMaxLength = shouldRotate ? LABEL_MAX_LENGTH / 2 : LABEL_MAX_LENGTH;

  const truncateIfNeeded = (label) => {
    if (label.length > labelMaxLength) {
      return `${label.substring(0, labelMaxLength)}…`;
    }
    return label;
  };

  const segmentWithValues = segments.find(element => element.uniqueCount > 0);

  const categoryFormatter = (value) => {
    const val = truncateIfNeeded(value);
    if (segments.length <= 1) {
      return val;
    }

    if (!segmentWithValues) {
      return val;
    }

    const { values } = segmentWithValues;
    const iconValue = values.find(v => v.name === value);

    const icon = getAxisIcon(question, iconValue);

    return `${icon}${val}`;
  };

  return {
    data,
    type: 'category',
    show: true,
    inverse: !isVerticalBars,
    axisLabel: {
      interval: 0,
      rotate: shouldRotate ? 45 : 0,
      formatter: categoryFormatter,
      rich: {
        veryBad: { ...iconBase, backgroundColor: GRADE_COLORS[1] },
        bad: { ...iconBase, backgroundColor: GRADE_COLORS[2] },
        neutral: { ...iconBase, backgroundColor: GRADE_COLORS[3] },
        good: { ...iconBase, backgroundColor: GRADE_COLORS[4] },
        veryGood: { ...iconBase, backgroundColor: GRADE_COLORS[5] },
      },
    },
  };
};

const BarChart = (props) => {
  const {
    segments: allSegments,
    isScoreGraph,
    chartSettings = {},
    onChartSettingsChange,
    toolbox,
    question,
  } = props;

  const { formatMessage, locale } = useIntl();

  const { switchedAxis, order } = chartSettings;

  const showAsPercentage = chartSettings.displayMode === 'percentage' && !isScoreGraph;

  const segments = isScoreGraph ? allSegments : allSegments.filter(s => !s.isBenchmark);

  const tooltip = {
    trigger: 'item',
    formatter({ name, seriesName, data }) {
      const formattedValue = tooltipFormatter({ data });
      const formattedSegment = formatSegment(getSegment(segments, seriesName), { lineBreak: '<br />', formatMessage, locale, ...props });

      return `${name}: ${formattedValue}<br />${formattedSegment}`;
    },
  };

  const categoryAxis = getCategoryAxis(segments, question, switchedAxis);
  const valueAxis = getValueAxis({ showAsPercentage });

  const barCount = categoryAxis.data.length * segments.length;
  const verticalLabels = false; // switchedAxis && barCount > BAR_COUNT_TO_ROTATE_LABEL;

  const defaultFontSize = parseInt(theme.fontSizeNormal, 10);
  let fontSize = defaultFontSize - (Math.round(barCount / defaultFontSize) - 1);
  if (barCount > 30) {
    fontSize = parseInt(theme.fontSizeSmallest, 10);
  }

  const series = segments.map((segment, index) => {
    let data = segment.values.map((v) => {
      const numericValue = v.value;
      const percentageValue = percent(numericValue, segment.uniqueCount);
      const value = showAsPercentage ? percentageValue : numericValue;
      return ({
        ...v,
        numericValue,
        percentageValue,
        value: switchedAxis ? [v.name, value] : [value, v.name],
      });
    });

    if (segments.length === 1) {
      data = colorChartParts(question, data, isScoreGraph);
    }
    // Used for sorting.
    const dateRange = `${segment.dateRange.startDate} - ${segment.dateRange.endDate}`;

    return {
      type: 'bar',
      name: uniqueSegmentId(segment),
      dateRange,
      showAllSymbol: 'auto',
      connectNulls: false,
      barMaxWidth: 200,
      barGap: '25%',
      barCategoryGap: '10%',
      color: segment.color || CHART_COLORS[index],
      data,
      label: {
        show: true,
        fontSize,
        color: BLACK,
        rotate: verticalLabels ? 90 : 0,
        align: verticalLabels ? 'left' : undefined,
        verticalAlign: verticalLabels ? 'middle' : undefined,
        position: switchedAxis ? 'top' : 'right',
        formatter: labelFormatter(showAsPercentage),
      },
    };
  });

  const legend = {
    data: [...new Set(series.map(s => s.name))],
    formatter: (value) => formatSegment(getSegment(segments, value), { formatMessage, locale }),
  };

  const option = {
    grid: getGridOptions(),
    legend,
    tooltip,
    xAxis: switchedAxis ? categoryAxis : valueAxis,
    yAxis: switchedAxis ? valueAxis : categoryAxis,
    series,
  };

  if (option.series.length > 1) {
    if (order === 'asc') {
      option.series.sort((a, b) => a.dateRange.localeCompare(b.dateRange) || a.name.localeCompare(b.name));
    } else if (order === 'desc') {
      option.series.sort((a, b) => b.dateRange.localeCompare(a.dateRange) || b.name.localeCompare(a.name));
    }
  } else if (order === 'asc') {
    option.series[0].data.sort((a, b) => a.value[switchedAxis ? 1 : 0] - b.value[switchedAxis ? 1 : 0]);
    option[switchedAxis ? 'xAxis' : 'yAxis'].data = option.series[0].data.map(d => d.value[switchedAxis ? 0 : 1]);
  } else if (order === 'desc') {
    option.series[0].data.sort((a, b) => b.value[switchedAxis ? 1 : 0] - a.value[switchedAxis ? 1 : 0]);
    option[switchedAxis ? 'xAxis' : 'yAxis'].data = option.series[0].data.map(d => d.value[switchedAxis ? 0 : 1]);
  }

  let height;

  if (!switchedAxis) {
    height = categoryAxis.data.length * segments.filter(s => isScoreGraph || !s.isBenchmark).length * 15 + 200;
  }

  const sortAxisAndData = () => {
    let newOrder;

    switch (order) {
      case 'asc':
        newOrder = 'desc';
        break;
      case 'desc':
        newOrder = null;
        break;
      default:
        newOrder = 'asc';
    }

    onChartSettingsChange({ order: newOrder });
  };

  return (
    <Chart
      {...props}
      height={height}
      option={option}
      toolbox={{
        ...toolbox,
        switchAxis: () => onChartSettingsChange({ switchedAxis: !switchedAxis }),
        sortAxisAndData,
      }}
    />
  );
};

export default BarChart;
